import type { JSX } from "react";
import { useEffect, useState } from "react";
import type { AssistantTurn, Citation, ResolutionStepState, SuggestionAction, UserTurn } from "@shared/types";
import { IconGlobe, IconClock } from "../icons";
import { CitationChip } from "./CitationChip";
import { isPublicCitation, stripHiddenCiteMarkers, stripThinkTags } from "../../lib/citations";
import { ToolCallGroup, CodeToolGroup } from "./ToolCallCard";
import { Markdown } from "./Markdown";
import { WidgetRenderer } from "./WidgetRenderer";
import { AnswerActions } from "./AnswerActions";
import { TryItYourself } from "../loom/LiveRunBlock";
import { getBlockRenderer } from "./blocks";

/**
 * Both message bubble variants. Per OpenSpec section 4.3.1 the AI
 * assistant turn enforces the five-part structure: direct answer,
 * numbered steps, conditions callout, next step, citations.
 */

/**
 * Full-screen lightbox for an uploaded image (#80). Opens on click of a
 * thumbnail so screenshots are legible at full size. Closes on the close
 * button, a backdrop click, or Escape. Uses the design-system overlay tokens
 * (--overlay-backdrop / --image-control-bg / --text-on-overlay).
 */
function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="image-lightbox" role="dialog" aria-modal="true" aria-label="图片预览" onClick={onClose}>
      <button type="button" className="image-lightbox-close" aria-label="关闭" onClick={onClose}>
        ×
      </button>
      <img className="image-lightbox-img" src={src} alt="放大后的图片" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

export function UserBubble({ turn }: { turn: UserTurn }): JSX.Element {
  // Which uploaded image (if any) is open in the full-screen lightbox (#80).
  const [zoomed, setZoomed] = useState<string | null>(null);
  return (
    <div className="turn turn-user">
      {turn.images && turn.images.length > 0 && (
        <div className="user-images">
          {turn.images.map((img, i) => (
            <button
              key={i}
              type="button"
              className="user-image-btn"
              onClick={() => setZoomed(img.dataUrl)}
              aria-label="放大查看图片"
            >
              <img className="user-image" src={img.dataUrl} alt="用户上传的图片" />
            </button>
          ))}
        </div>
      )}
      {turn.text.length > 0 && <div className="user-bubble">{turn.text}</div>}
      {zoomed !== null && <ImageLightbox src={zoomed} onClose={() => setZoomed(null)} />}
    </div>
  );
}

interface AssistantBubbleProps {
  turn: AssistantTurn;
  activeCitationId: string | null;
  onCitationClick: (citation: Citation) => void;
  onSendPrompt: (text: string) => void;
  onFeedback?: (reason: string, note?: string) => void;
  /** Persist a resolution step-state change (Feature 2 resolution panel). */
  onMutateStep?: (blockId: string, stepId: string, state: ResolutionStepState) => void;
  /** Dispatch a next-steps connect/upload action (P4c). */
  onSuggestionAction?: (action: SuggestionAction) => void;
  /** Open a generated artifact in the side canvas (FEATURE_ARTIFACTS · #7 · 7a). */
  onOpenArtifact?: (artifactId: string) => void;
  /** The user question this answer responded to (§2-1 regenerate + reasons). */
  precedingQuestion?: string;
  /** Active conversation session id, so a Try-it-yourself run can persist (L-2). */
  conversationId?: string;
  /** Record a thumbs rating for this answer (§2-1). Reasons are multi-select.
   * A `null` rating retracts a prior rating (§3.2). */
  onRate?: (
    turnId: string,
    rating: "up" | "down" | null,
    reasons?: string[],
    note?: string,
    screenshot?: { data: string; mimeType: string }
  ) => void;
  /** Escalate this answer to a human (§2-1). */
  onEscalate?: (turnId: string) => void;
}

/** Seconds since `startMs`, ticking once a second while `active`. */
function useElapsedSeconds(startMs: number, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return Math.max(0, Math.round((now - startMs) / 1000));
}

/** Stage label for the in-progress indicator, derived from the latest tool. */
function progressLabel(turn: AssistantTurn): string {
  const last = turn.toolCalls[turn.toolCalls.length - 1];
  if (last === undefined) return "小环正在思考…";
  if (last.status === "running") {
    if (last.kind === "web") return "正在联网检索…";
    if (last.kind === "kb") return "正在检索知识库…";
    return "正在处理…";
  }
  return "正在整理答案…"; // tools done, composing the reply
}

export function AssistantBubble({
  turn,
  activeCitationId,
  onCitationClick,
  onSendPrompt,
  onFeedback,
  onMutateStep,
  onSuggestionAction,
  onOpenArtifact,
  precedingQuestion,
  conversationId,
  onRate,
  onEscalate
}: AssistantBubbleProps): JSX.Element {
  const isWeb = turn.template === "WEB_FALLBACK";
  // Handoff turns carry a small "客服跟进中" status badge so the user reads it
  // as ongoing human follow-up, distinct from a normal AI answer (§20.9).
  const isHandoff = turn.template === "HANDOFF";
  // Working indicator: show a contextual status (current tool / 正在整理答案…) +
  // elapsed time for the WHOLE duration the turn is streaming — not only before
  // the first token. The agent narrates a line, then may run many tool calls and
  // spend tens of seconds composing the final answer; without a persistent
  // indicator the UI looks frozen and the user can't tell it is still working.
  // Elapsed timer reassures the user during longer retrieval/generation waits.
  const elapsed = useElapsedSeconds(turn.timestamp, turn.streaming === true);
  // Real answer sources are shown first and prominently; low-confidence
  // near-misses are kept in a separate, clearly-labeled group so they are never
  // read as grounding for the answer (§1.11.1 / codex 2026-06-02 §1).
  // Only public, clickable web sources are shown (isPublicCitation); internal
  // KB / manual / case / video sources are hidden, and their inline 【来源N】
  // markers are stripped so the answer never points at a hidden chip.
  const realCitations = turn.citations.filter((c) => c.lowConfidence !== true && isPublicCitation(c));
  const lowCitations = turn.citations.filter((c) => c.lowConfidence === true && isPublicCitation(c));
  // P4 provenance: the caller's OWN materials that grounded this answer. Their KB
  // citations are otherwise hidden (isPublicCitation), so this is the ONLY place
  // the user SEES that their uploaded / connected content was actually used.
  const personalUsed = [
    ...new Set(turn.citations.filter((c) => c.origin === "personal").map((c) => c.title))
  ];
  // Stage 4a: the caller's OWN connected Feishu docs that grounded the answer.
  // Deduped by url so the provenance can link back to the live doc.
  const feishuUsed = Array.from(
    new Map(
      turn.citations
        .filter((c) => c.origin === "feishu-user")
        .map((c) => [c.url ?? c.title, { title: c.title, url: c.url }] as const)
    ).values()
  );
  // 7b: the caller's OWN promoted artifacts (generated docs added to knowledge)
  // that grounded the answer — provenance「小环生成」, same as personal/feishu.
  const artifactUsed = [
    ...new Set(turn.citations.filter((c) => c.origin === "artifact").map((c) => c.title))
  ];
  const answerText = stripThinkTags(stripHiddenCiteMarkers(turn.directAnswer, turn.citations));
  // With FEATURE_IA_STRUCTURED_ANSWER on, a settled answer arrives as markdown
  // BLOCKS and directAnswer stays empty — the post-answer actions (rate/copy,
  // the product's feedback loop) must not vanish on those turns. Same both-modes
  // rule TryItYourself below already follows.
  const blockAnswerText = (turn.blocks ?? [])
    .map((b) => ("markdown" in b && typeof (b as { markdown?: unknown }).markdown === "string" ? (b as { markdown: string }).markdown : ""))
    .filter((s) => s.length > 0)
    .join("\n\n")
    .trim();
  return (
    <div className="turn turn-assistant">
      <span className="msg-avatar" aria-hidden="true">
        环
      </span>
      <div className="msg-body">
        {/* UI-only tools (show_*) render as widgets, not trace cards. Filter any
            that slipped through — e.g. sessions persisted before the server-side
            suppression, whose sidecar still holds a raw `show_*` label (#79). */}
        {/* Group consecutive same-label tool calls into one collapsible card so a
            code answer (many read_source / grep_source calls) is not preceded by a
            wall of cards. UI-only show_* tools render as widgets, not trace cards. */}
        {(() => {
          // 1. group consecutive same-label calls (e.g. read_source ×5)
          const labelGroups: (typeof turn.toolCalls)[] = [];
          for (const call of turn.toolCalls) {
            if (call.label.startsWith("show_")) continue;
            const last = labelGroups[labelGroups.length - 1];
            if (last?.[0] !== undefined && last[0].label === call.label) last.push(call);
            else labelGroups.push([call]);
          }
          // 2. fold consecutive code-Q&A groups under one neutral "代码检索" umbrella
          const items: { code: boolean; groups: (typeof turn.toolCalls)[] }[] = [];
          for (const g of labelGroups) {
            const isCode = g[0]?.kind === "code";
            const last = items[items.length - 1];
            if (isCode && last?.code === true) last.groups.push(g);
            else items.push({ code: isCode, groups: [g] });
          }
          return items.map((it) =>
            it.code ? (
              <CodeToolGroup key={it.groups[0]![0]!.id} groups={it.groups} />
            ) : (
              <ToolCallGroup key={it.groups[0]![0]!.id} calls={it.groups[0]!} />
            ),
          );
        })()}
        {isWeb && turn.webBanner && (
          <div className="web-banner">
            <IconGlobe size={12} />
            基于公开网络结果
          </div>
        )}
        {isHandoff && (
          <div className="handoff-badge">
            <IconClock size={12} />
            客服跟进中
          </div>
        )}
        {turn.directAnswer.length > 0 && (
          <div className="answer-direct">
            <Markdown>{answerText}</Markdown>
            {turn.streaming === true && <span className="stream-cursor" />}
          </div>
        )}
        {turn.streaming === true && (
          <div
            className="thinking"
            aria-live="polite"
            style={turn.directAnswer.length > 0 ? { marginTop: "var(--space-2)" } : undefined}
          >
            <span className="thinking-dots">
              <span />
              <span />
              <span />
            </span>
            {progressLabel(turn)}
            {elapsed >= 2 && <span className="thinking-elapsed">{elapsed}s</span>}
          </div>
        )}
        {turn.steps && turn.steps.length > 0 && (
          <ol className="answer-steps">
            {turn.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        )}
        {turn.callout && (
          <div className="callout">
            <span className="callout-label">{turn.callout.label}</span>
            {turn.callout.body}
          </div>
        )}
        {turn.nextStep && (
          <div className="next-step">
            <span className="next-step-label">下一步建议</span>
            {turn.nextStep}
          </div>
        )}
        {personalUsed.length > 0 && (
          <div className="personal-used" data-testid="personal-used">
            <span className="pu-badge">你的资料</span>
            <span className="pu-text">参考了 {personalUsed.join("、")}</span>
          </div>
        )}
        {feishuUsed.length > 0 && (
          <div className="personal-used" data-testid="feishu-used">
            <span className="pu-badge">你的飞书</span>
            <span className="pu-text">
              参考了{" "}
              {feishuUsed.map((f, i) => (
                <span key={f.url ?? f.title}>
                  {i > 0 && "、"}
                  {f.url !== undefined ? (
                    <a href={f.url} target="_blank" rel="noreferrer">
                      {f.title}
                    </a>
                  ) : (
                    f.title
                  )}
                </span>
              ))}
            </span>
          </div>
        )}
        {artifactUsed.length > 0 && (
          <div className="personal-used" data-testid="artifact-used">
            <span className="pu-badge">小环生成</span>
            <span className="pu-text">参考了 {artifactUsed.join("、")}</span>
          </div>
        )}
        {turn.webDisclaim && <div className="web-disclaim">{turn.webDisclaim}</div>}
        {realCitations.length > 0 && (
          <div className="citations">
            {lowCitations.length > 0 && (
              // Only label the real group when a low-confidence group also shows,
              // so the "用于作答 vs 可能相关" contrast is explicit (§2-3); a lone
              // sources row needs no heading.
              <span className="citations-label">用于作答的来源</span>
            )}
            {realCitations.map((c) => (
              <CitationChip
                key={c.id}
                citation={c}
                active={activeCitationId === c.id}
                onClick={onCitationClick}
              />
            ))}
          </div>
        )}
        {lowCitations.length > 0 && (
          <div className="citations citations-low">
            <span className="citations-low-label">可能相关 · 低置信，仅供参考</span>
            {lowCitations.map((c) => (
              <CitationChip
                key={c.id}
                citation={c}
                active={activeCitationId === c.id}
                onClick={onCitationClick}
              />
            ))}
          </div>
        )}
        {/* Native answer blocks (Feature 2), rendered through the registry. An
            unknown type is skipped rather than throwing, so a newer server
            block degrades gracefully on an older client. */}
        {(turn.blocks ?? []).map((b) => {
          const Renderer = getBlockRenderer(b.type);
          if (Renderer === undefined) return null;
          return (
            <Renderer
              key={b.id}
              block={b}
              onSendPrompt={onSendPrompt}
              onMutateStep={onMutateStep ?? (() => undefined)}
              citations={turn.citations}
              {...(conversationId !== undefined ? { conversationId } : {})}
              {...(onSuggestionAction !== undefined ? { onSuggestionAction } : {})}
              {...(onOpenArtifact !== undefined ? { onOpenArtifact } : {})}
            />
          );
        })}
        {(turn.widgets ?? []).map((w) => (
          <div key={w.id} className="widget-card">
            <div className="widget-card-head">{w.title}</div>
            <WidgetRenderer html={w.html} onSendPrompt={onSendPrompt} onFeedback={onFeedback} title={w.title} />
          </div>
        ))}
        {/* Post-answer actions on a settled, substantive answer (§2-1) — plain
            directAnswer OR markdown blocks. Not on handoff turns (already with
            a human) or while still streaming. */}
        {turn.streaming !== true &&
          !isHandoff &&
          (turn.directAnswer.trim().length > 0 || blockAnswerText.length > 0) &&
          onRate !== undefined &&
          onEscalate !== undefined && (
            <AnswerActions
              turnId={turn.id}
              answer={turn.directAnswer.trim().length > 0 ? turn.directAnswer : blockAnswerText}
              precedingQuestion={precedingQuestion ?? ""}
              initialRating={turn.rating}
              initialReasons={turn.ratingReasons}
              onRegenerate={onSendPrompt}
              onEscalate={onEscalate}
              onRate={onRate}
            />
          )}
        {/* Answer-inline "Try it yourself": run the question in the user's own
            织灵 (only shown when connected; renders nothing otherwise). L-2.
            A settled answer may arrive as directAnswer OR as native blocks
            (markdown / web-fallback) — accept either so the CTA isn't missing on
            block-based answers. conversationId + responseId let the run persist. */}
        {turn.streaming !== true &&
          !isHandoff &&
          (turn.directAnswer.trim().length > 0 || (turn.blocks?.length ?? 0) > 0) &&
          precedingQuestion !== undefined &&
          precedingQuestion !== "" && (
            <TryItYourself
              prompt={precedingQuestion}
              runnable={turn.runnable === true}
              {...(conversationId !== undefined ? { conversationId } : {})}
            />
          )}
      </div>
    </div>
  );
}
